// SectionEyebrow — the brand's signature header device (DESIGN-NOTES / plan §5.2):
// a wide letter-spaced ALL-CAPS Latin caption (the "eyebrow") paired with a large
// bold heading. Locale-aware ordering: in zh the Chinese heading leads and the Latin
// caption follows below; in en the caption sits above (classic eyebrow).
//
// Usage (mirrors SectionBlock — caption + a LOCALE-RESOLVED heading):
//   <SectionEyebrow eyebrowEn={t("eyebrow")} heading={t("title")} />
//   <SectionEyebrow eyebrowEn={t("eyebrow")} heading={t("title")} as="h1" align="center" />
import { useTranslation } from "react-i18next";

import { SUPPORTED_LOCALES, type AppLocale, DEFAULT_LOCALE } from "../i18n";

type HeadingTag = "h1" | "h2" | "h3";

export interface SectionEyebrowProps {
  /** Wide letter-spaced ALL-CAPS Latin caption (the brand eyebrow device). */
  eyebrowEn: string;
  /** Locale-resolved heading text (caller passes t("...") so it's zh or en). */
  heading: string;
  /** Heading element to render. Defaults to h2. */
  as?: HeadingTag;
  /** Text alignment of the block. */
  align?: "left" | "center";
  className?: string;
}

function isLocale(v: string | undefined): v is AppLocale {
  return !!v && (SUPPORTED_LOCALES as readonly string[]).includes(v);
}

export default function SectionEyebrow({
  eyebrowEn,
  heading,
  as: Heading = "h2",
  align = "left",
  className,
}: SectionEyebrowProps) {
  const { i18n } = useTranslation();
  const active: AppLocale = isLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE;
  const isZh = active === "zh";

  // Hide the caption when it's just the heading in a different case — otherwise the
  // device renders a duplicate title (e.g. "CONTACT US" caption over a "Contact Us"
  // heading in en). In zh the caption (Latin) always differs from the heading (CJK).
  const showCaption =
    eyebrowEn.trim().toLowerCase() !== heading.trim().toLowerCase();

  const caption = showCaption ? (
    <p
      className={`font-sans text-[var(--text-sm)] font-medium uppercase tracking-[var(--tracking-eyebrow)] text-[var(--brand-azure-text)]${
        isZh ? " mt-3" : ""
      }${align === "center" ? " mx-auto" : ""}`}
    >
      {eyebrowEn}
    </p>
  ) : null;
  const head = (
    <Heading
      className={`font-zh text-[var(--text-3xl)] font-bold leading-[var(--leading-heading)] text-[var(--ink)]${
        !isZh && showCaption ? " mt-4" : ""
      }`}
    >
      {heading}
    </Heading>
  );

  return (
    <div className={`${align === "center" ? "text-center" : "text-left"}${className ? ` ${className}` : ""}`}>
      {isZh ? (
        <>
          {head}
          {caption}
        </>
      ) : (
        <>
          {caption}
          {head}
        </>
      )}
    </div>
  );
}
